// Confeti y pirotecnia al registrar un pedido. Dibuja en un lienzo propio pegado al <body> (no en el árbol de Preact),
// así sigue aunque la pantalla cambie al compartir el pedido. No tapa nada ni recibe clics. Con "reducir movimiento"
// en el dispositivo solo se muestra el mensaje. La parte que decide qué tan grande es está en logica/celebracion.ts.
import { PLANES, type NivelCelebracion, type PlanCelebracion } from '../logica/celebracion';

const COLORES = ['#FF9A6B', '#1A5FD0', '#6FA8FF', '#FFC9B0', '#FFD166', '#4CC38A', '#FF6F91', '#A78BFA'];
const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const elige = <T,>(xs: T[]) => xs[Math.floor(Math.random() * xs.length)];

interface Confeti { x: number; y: number; vx: number; vy: number; rot: number; vr: number; w: number; h: number; color: string; espera: number; fase: number }
interface Chispa { x: number; y: number; vx: number; vy: number; vida: number; edad: number; r: number; color: string }
interface Estallido { en: number; x: number; y: number; color: string; lanzado: boolean }

let limpiar: (() => void) | null = null;   // quita la celebración que esté en pantalla
let quitarAviso: number | null = null;

function aviso(texto: string, nivel: NivelCelebracion, ms: number) {
  let r = document.getElementById('celebracion-aviso');
  if (!r) {
    r = document.createElement('div');
    r.id = 'celebracion-aviso'; r.className = 'celebra-aviso';
    r.setAttribute('role', 'status'); r.setAttribute('aria-live', 'polite');
    document.body.appendChild(r);
  }
  const caja = r;
  caja.className = 'celebra-aviso'; caja.textContent = '';
  if (quitarAviso != null) clearTimeout(quitarAviso);
  // El texto entra un instante después para que los lectores de pantalla lo lean como cambio de una región ya presente.
  setTimeout(() => { caja.dataset.nivel = nivel; caja.textContent = texto; caja.classList.add('visible'); }, 30);
  quitarAviso = window.setTimeout(() => { caja.classList.remove('visible'); quitarAviso = window.setTimeout(() => { caja.textContent = ''; delete caja.dataset.nivel; }, 400); }, ms);
}

function lienzo(plan: PlanCelebracion, nivel: NivelCelebracion) {
  const cv = document.createElement('canvas');
  cv.id = 'celebracion-lienzo'; cv.dataset.nivel = nivel; cv.setAttribute('aria-hidden', 'true');
  cv.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:1000';
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let W = window.innerWidth, H = window.innerHeight;
  cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
  document.body.appendChild(cv);
  const g = cv.getContext('2d');
  if (!g) { cv.remove(); return null; }
  g.scale(dpr, dpr);

  const dur = plan.duracion / 1000;
  const confeti: Confeti[] = Array.from({ length: plan.confeti }, () => ({
    x: rnd(0, W), y: rnd(-40, -10), vx: rnd(-70, 70), vy: rnd(120, 340), rot: rnd(0, 6.28), vr: rnd(-7, 7),
    w: rnd(6, 11), h: rnd(10, 18), color: elige(COLORES), espera: rnd(0, dur * 0.55), fase: rnd(0, 6.28),
  }));
  const estallidos: Estallido[] = Array.from({ length: plan.estallidos }, (_, i) => ({
    en: 0.15 + (i / Math.max(1, plan.estallidos - 1)) * (dur * 0.6) + rnd(-0.08, 0.08), x: rnd(W * 0.12, W * 0.88), y: rnd(H * 0.12, H * 0.5), color: elige(COLORES), lanzado: false,
  }));
  const chispas: Chispa[] = [];
  const lanzar = (e: Estallido) => {
    for (let k = 0; k < plan.chispas; k++) {
      const a = rnd(0, 6.283), v = rnd(70, 330) * (k % 3 === 0 ? 0.55 : 1);
      chispas.push({ x: e.x, y: e.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, vida: rnd(0.8, 1.4), edad: 0, r: rnd(1.6, 3.4), color: k % 5 === 0 ? '#FFFFFF' : e.color });
    }
  };

  let t0 = 0, ultimo = 0, raf = 0, fin = false;
  const terminar = () => { if (fin) return; fin = true; cancelAnimationFrame(raf); clearTimeout(seguro); cv.remove(); window.removeEventListener('resize', ajustar); if (limpiar === terminar) limpiar = null; };
  const seguro = window.setTimeout(terminar, plan.duracion + 800);
  const ajustar = () => { W = window.innerWidth; H = window.innerHeight; cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr); g.setTransform(dpr, 0, 0, dpr, 0, 0); };
  window.addEventListener('resize', ajustar);

  const cuadro = (ahora: number) => {
    if (fin) return;
    if (!t0) { t0 = ultimo = ahora; }
    const t = (ahora - t0) / 1000, dt = Math.min(0.05, (ahora - ultimo) / 1000);
    ultimo = ahora;
    if (t >= dur) { terminar(); return; }
    // Desvanecer el último tramo (450 ms) para que no corte de golpe.
    const alfa = Math.min(1, (dur - t) / 0.45);
    g.clearRect(0, 0, W, H);
    for (const e of estallidos) if (!e.lanzado && t >= e.en) { e.lanzado = true; lanzar(e); }
    g.globalAlpha = alfa;
    for (const c of confeti) {
      if (t < c.espera) continue;
      c.vy = Math.min(c.vy + 160 * dt, 460);
      c.x += (c.vx + Math.sin(t * 3 + c.fase) * 40) * dt; c.y += c.vy * dt; c.rot += c.vr * dt;
      if (c.y > H + 30) continue;
      g.save(); g.translate(c.x, c.y); g.rotate(c.rot); g.scale(1, Math.cos(c.rot * 1.3)); g.fillStyle = c.color; g.fillRect(-c.w / 2, -c.h / 2, c.w, c.h); g.restore();
    }
    for (const s of chispas) {
      s.edad += dt; if (s.edad >= s.vida) continue;
      s.vx *= 0.985; s.vy = s.vy * 0.985 + 260 * dt; s.x += s.vx * dt; s.y += s.vy * dt;
      const f = 1 - s.edad / s.vida;
      g.globalAlpha = alfa * f; g.fillStyle = s.color; g.beginPath(); g.arc(s.x, s.y, s.r * (0.4 + 0.6 * f), 0, 6.283); g.fill();
    }
    g.globalAlpha = 1;
    raf = requestAnimationFrame(cuadro);
  };
  raf = requestAnimationFrame(cuadro);
  return terminar;
}

// Muestra la celebración del nivel dado con el mensaje. Si ya había una, la reemplaza.
export function celebrar(nivel: NivelCelebracion, mensaje: string) {
  const plan = PLANES[nivel];
  if (limpiar) limpiar();
  aviso(mensaje, nivel, plan.aviso);
  const quieto = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!quieto) limpiar = lienzo(plan, nivel);
}
